import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DateField } from './form/DateField';
import { TimeField } from './form/TimeField';
import { dollarsToCents, centsToDollars } from '@/utils/money';
import { 
  BookOpen, 
  MapPin, 
  Users, 
  DollarSign, 
  Calendar, 
  Info, 
  Settings,
  Clock,
  Tag,
  CheckCircle2,
  Save,
  X
} from 'lucide-react';

export const EventFormSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  description: z.string().optional(),
  location_id: z.string().optional(), // Changed from location to location_id
  startDate: z.date({ required_error: 'Start date is required' }),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  billing_model: z.enum(['one_time', 'per_attendee', 'free', 'custom'], { required_error: 'Billing model is required' }),
  price_dollars: z.string().refine(
    (val) => val === '' || /^\d+(\.\d{1,2})?$/.test(val), 
    'Enter a valid dollar amount'
  ),
  capacity: z.number().min(1, 'Max event size must be at least 1'),
  category: z.string().optional(),
  status: z.enum(['active', 'inactive', 'draft', 'archived']).optional(),
  trial_available: z.boolean().optional(),
  is_active: z.boolean().optional(),
});

export type EventFormValues = z.infer<typeof EventFormSchema>;

export interface EventFormProps {
  onSubmit: (values: EventFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  defaultValues?: Partial<EventFormValues>;
  locationOptions?: { value: string; label: string }[];
}

const EventForm: React.FC<EventFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  defaultValues,
  locationOptions = []
}) => {
  // Convert price_cents to dollars for display
  const displayDefaults = defaultValues ? {
    ...defaultValues,
    price_dollars: (defaultValues as any).price_cents !== undefined ? centsToDollars((defaultValues as any).price_cents) : '',
    location_id: defaultValues.location_id || 'none',
  } : undefined;

  const form = useForm<EventFormValues>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: displayDefaults || {
      name: '',
      description: '',
      location_id: 'none',
      startDate: new Date(),
      startTime: '',
      endTime: '',
      billing_model: 'one_time',
      price_dollars: '',
      capacity: 1,
    }
  });

  // On submit, convert price_dollars from dollars to cents
  const handleSubmit = form.handleSubmit((values) => {
    // Convert empty price to 0 cents, otherwise convert to cents
    const price_cents = values.price_dollars === '' ? 0 : dollarsToCents(values.price_dollars);
    const submitValues = { 
      ...values, 
      price_cents,
      location_id: values.location_id === 'none' ? '' : values.location_id,
    };
    delete submitValues.price_dollars;
    onSubmit(submitValues as any);
  });

  // Handle numeric input conversions
  const handleCapacityChange = (value: string) => {
    const numericValue = parseInt(value, 10);
    form.setValue('capacity', isNaN(numericValue) ? 1 : numericValue);
  };

  const billingModel = form.watch('billing_model');
  const trialAvailable = form.watch('trial_available');
  const isActive = form.watch('is_active');

  const priceLabel = {
    one_time: 'Total Event Price',
    per_attendee: 'Price Per Attendee',
    free: 'No Charge',
    custom: 'Custom Pricing',
  }[billingModel || 'one_time'];

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* General Information */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Event Information</CardTitle>
                <p className="text-sm text-muted-foreground">Basic event details and description</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Event Name *
                </Label>
                <Input
                  id="name"
                  placeholder="Enter event name"
                  {...form.register('name', { required: true })}
                  className="h-11"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-medium flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Category
                </Label>
                <Input
                  id="category"
                  placeholder="e.g., Workshop, Performance, Competition"
                  {...form.register('category')}
                  className="h-11"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this event is about, what participants will experience, etc."
                {...form.register('description')}
                className="min-h-[100px] resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Location & Schedule */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Location & Schedule</CardTitle>
                <p className="text-sm text-muted-foreground">Where and when this event takes place</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm font-medium">Location</Label>
              <Select
                value={form.watch('location_id') || 'none'}
                onValueChange={(value) => form.setValue('location_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a location</SelectItem>
                  {locationOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and time fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Event Date *
                </Label>
                <DateField 
                  form={form}
                  name="startDate" 
                  label=""
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Start Time
                </Label>
                <TimeField 
                  form={form}
                  name="startTime" 
                  label=""
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  End Time
                </Label>
                <TimeField 
                  form={form}
                  name="endTime" 
                  label=""
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Capacity & Pricing */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Capacity & Pricing</CardTitle>
                <p className="text-sm text-muted-foreground">Set event limits and pricing structure</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="capacity" className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Max Attendees
              </Label>
              <Input
                id="capacity"
                type="number"
                min={1}
                value={form.watch('capacity') || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    form.setValue('capacity', '');
                  } else {
                    handleCapacityChange(value);
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    handleCapacityChange('1');
                  }
                }}
                className="h-11"
              />
              {form.formState.errors.capacity && (
                <p className="text-sm text-destructive">{form.formState.errors.capacity.message}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="billing_model" className="text-sm font-medium">Billing Model</Label>
                <Select
                  value={form.watch('billing_model') || 'one_time'}
                  onValueChange={(value) => form.setValue('billing_model', value as any)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select billing model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time (total event)</SelectItem>
                    <SelectItem value="per_attendee">Per Attendee</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.billing_model && (
                  <p className="text-sm text-destructive">{form.formState.errors.billing_model.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price" className="text-sm font-medium">{priceLabel} (USD)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="price"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00 (optional)"
                    {...form.register('price_dollars')}
                    disabled={billingModel === 'free'}
                    className="h-11 pl-10"
                  />
                </div>
                {form.formState.errors.price_dollars && (
                  <p className="text-sm text-destructive">{form.formState.errors.price_dollars.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings & Status */}
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Settings className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Settings & Status</CardTitle>
                <p className="text-sm text-muted-foreground">Configure event workflow status and enrollment visibility</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="status" className="text-sm font-medium">Workflow Status</Label>
              <Select
                value={form.watch('status') || 'active'}
                onValueChange={(value) => form.setValue('status', value as any)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-yellow-400" />
                      Draft
                    </div>
                  </SelectItem>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Active
                    </div>
                  </SelectItem>
                  <SelectItem value="inactive">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-gray-400" />
                      Inactive
                    </div>
                  </SelectItem>
                  <SelectItem value="archived">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-400" />
                      Archived
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Workflow status for internal management. Draft = Work in progress, Active = Ready for registration, Inactive = Temporarily unavailable, Archived = No longer offered.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3 p-4 border rounded-lg bg-gray-50/50">
                <Checkbox
                  id="trial_available"
                  checked={trialAvailable || false}
                  onCheckedChange={(checked) => form.setValue('trial_available', !!checked)}
                />
                <div className="space-y-1">
                  <Label htmlFor="trial_available" className="text-sm font-medium cursor-pointer">
                    Allow Free Trial
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Participants can try this event for free before registering
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 border rounded-lg bg-gray-50/50">
                <Checkbox
                  id="is_active"
                  checked={isActive !== undefined ? isActive : true}
                  onCheckedChange={(checked) => form.setValue('is_active', !!checked)}
                />
                <div className="space-y-1">
                  <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer">
                    Visible to Participants
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show this event to participants in registration listings (independent of workflow status)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Event
              </>
            )}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default EventForm;
